(function () {
  if (customElements.get('ce-slideshow') !== undefined) {
    return;
  }

  class Slideshow extends HTMLParsedElement {
    constructor() {
      super();

      this.queuedAttributeChangedCallbackParams = [];

      this.options = {
        alignment: this.hasAttribute('alignment') ? this.getAttribute('alignment') : 'left',
        duration: this.hasAttribute('duration') ? parseInt(this.getAttribute('duration')) : 3000,
        disabledOnDesktop: this.hasAttribute('disabled-on-desktop') || false,
        disabledOnMobile: this.hasAttribute('disabled-on-mobile') || false,
        isAnimated: this.hasAttribute('is-animated') || false,
        maximumWidth: this.hasAttribute('maximum-width') ? this.getAttribute('maximum-width') : 'content',
        slidesToShowOnDesktop: this.hasAttribute('slides-to-show-on-desktop') ? parseInt(this.getAttribute('slides-to-show-on-desktop')) : 1,
        slidesToShowOnMobile: this.hasAttribute('slides-to-show-on-mobile') ? parseInt(this.getAttribute('slides-to-show-on-mobile')) : 1,
        threshold: this.hasAttribute('threshold') ? parseFloat(this.getAttribute('threshold')) : 0.5,
        variableWidthSlides: this.hasAttribute('variable-width-slides') || false,
      };

      this.selectors = {
        navigation: '[data-slideshow-navigation]',
        navigationListContainer: '[data-slideshow-navigation-list-container]',
        navigationList: '[data-slideshow-navigation-list]',
        navigationButton: '[data-slideshow-navigation-button]',
        navigationButtonPrevious: '[data-slideshow-navigation-button][data-action="previous"]',
        navigationButtonNext: '[data-slideshow-navigation-button][data-action="next"]',
        list: '[data-slideshow-list]',
        listItemAnimationTarget: '[data-slideshow-list-item-animation-target]',
        mouseTarget: '[data-slideshow-mouse-target]',
        progressBar: '[data-slideshow-progress-bar]',
      };

      this._handleMouseEnter = this._handleMouseEnter.bind(this);
      this._handleMouseLeave = this._handleMouseLeave.bind(this);
      this._handleFocusIn = this._handleFocusIn.bind(this);
      this._handleFocusOut = this._handleFocusOut.bind(this);
      this._handleClickNavigationButton = this._handleClickNavigationButton.bind(this);
    }

    get disabled() {
      return this.getAttribute('disabled') === 'true';
    }

    set disabled(value) {
      this.setAttribute('disabled', value);
    }

    get playState() {
      return this.getAttribute('play-state');
    }

    set playState(value) {
      this.setAttribute('play-state', value);
    }

    get totalSlides() {
      return parseInt(this.getAttribute('total-slides'));
    }

    set totalSlides(value) {
      this.setAttribute('total-slides', value);
    }

    get viewport() {
      return this.getAttribute('viewport');
    }

    set viewport(value) {
      this.setAttribute('viewport', value);
    }

    get visibleSlides() {
      return JSON.parse(this.getAttribute('visible-slides'));
    }

    set visibleSlides(value) {
      this.setAttribute('visible-slides', JSON.stringify(value));
    }

    static get observedAttributes() {
      return ['disabled', 'play-state', 'total-slides', 'viewport', 'visible-slides'];
    }

    attributeChangedCallback(...params) {
      if (!this.parsed) {
        this.queuedAttributeChangedCallbackParams.push(params);
        return;
      }

      const [name, oldValue, newValue] = params;

      if (oldValue === newValue) {
        return;
      }

      switch (name) {
        case 'disabled':
          if (newValue !== null) {
            if (this.disabled) {
              this._disable();
            } else {
              this._enable();
            }
          }
          break;
        case 'play-state':
          if (newValue !== null) {
            switch (this.playState) {
              case 'running':
                this.progressBarAnimation?.play();
                this.elements.list.setAttribute('aria-live', 'off');
                break;
              case 'paused':
                this.progressBarAnimation?.pause();
                this.elements.list.setAttribute('aria-live', 'polite');
                break;
              case 'disabled':
                this.progressBarAnimation?.cancel();
                this.elements.list.setAttribute('aria-live', 'polite');
                break;
            }
          }
          break;
        case 'total-slides':
          if (this.elements.navigation !== null && !this.options.variableWidthSlides) {
            if (this.viewport === 'md') {
              const numSlides = Math.min(this.totalSlides, this.options.slidesToShowOnDesktop + 4);
              this.elements.navigationListContainer.style.setProperty('--width', `${numSlides * 16}px`);
            } else {
              const numSlides = Math.min(this.totalSlides, this.options.slidesToShowOnMobile + 4);
              this.elements.navigationListContainer.style.setProperty('--width', `${numSlides * 16}px`);
            }
          }
          break;
        case 'viewport':
          if (newValue !== null) {
            if (this.viewport === 'default' && this.options.disabledOnMobile) {
              this.disabled = true;
              break;
            }

            if (this.viewport === 'md' && this.options.disabledOnDesktop) {
              this.disabled = true;
              break;
            }

            this.disabled = false;

            if (this.elements.navigation !== null && !this.options.variableWidthSlides) {
              if (this.viewport === 'md') {
                this.elements.navigationListContainer.style.setProperty('--width', `${Math.min(this.totalSlides, this.options.slidesToShowOnDesktop + 4) * 16}px`);
              } else {
                this.elements.navigationListContainer.style.setProperty('--width', `${Math.min(this.totalSlides, this.options.slidesToShowOnMobile + 4) * 16}px`);
              }
            }

            this.dispatchEvent(new CustomEvent('ce-slideshow:viewport', { detail: this.viewport }));
          }
          break;
        case 'visible-slides':
          const oldVisibleSlides = JSON.parse(oldValue) || [];
          const newVisibleSlides = JSON.parse(newValue) || [];

          this._updateProgressBarAnimation(newVisibleSlides);
          this._updateTargetPosition(newVisibleSlides);

          if (this.elements.navigation !== null) {
            this._updateNavigationList(oldVisibleSlides, newVisibleSlides);
            this._updateNavigationButtons(newVisibleSlides);
          }

          if (this.options.isAnimated) {
            this._animateSlideText(oldVisibleSlides, newVisibleSlides);
          }

          this._updateAriaAttributes(newVisibleSlides);

          this.dispatchEvent(new CustomEvent('ce-slideshow:visible-slides', { detail: newVisibleSlides }));

          break;
      }
    }

    parsedCallback() {
      this.elements = {
        navigation: this.querySelector(this.selectors.navigation),
        navigationListContainer: this.querySelector(this.selectors.navigationListContainer),
        navigationList: this.querySelector(this.selectors.navigationList),
        navigationButtonPrevious: this.querySelector(this.selectors.navigationButtonPrevious),
        navigationButtonNext: this.querySelector(this.selectors.navigationButtonNext),
        list: this.querySelector(this.selectors.list),
        mouseTargets: this.querySelectorAll(this.selectors.mouseTarget),
      };

      this._initResizeObserver();
      this._initMediaQuery();

      for (let params of this.queuedAttributeChangedCallbackParams) {
        this.attributeChangedCallback(...params);
      }
    }

    disconnectedCallback() {
      this._disable();
    }

    _enable() {
      if (this.elements.navigation !== null) {
        requestAnimationFrame(() => {
          this.elements.navigation.classList.remove('hidden');
        });
      }

      this.resizeObserver?.observe(this.elements.list);

      if (this.elements.navigation !== null) {
        this.elements.navigationButtonPrevious.addEventListener('click', this._handleClickNavigationButton);
        this.elements.navigationButtonNext.addEventListener('click', this._handleClickNavigationButton);
      }

      if (this.playState !== 'disabled') {
        for (let mouseTarget of this.elements.mouseTargets) {
          mouseTarget.addEventListener('mouseenter', this._handleMouseEnter);
          mouseTarget.addEventListener('mouseleave', this._handleMouseLeave);
        }

        this.addEventListener('focusin', this._handleFocusIn);
        this.addEventListener('focusout', this._handleFocusOut);
      }

      this.elements.list.setAttribute('aria-live', 'polite');
    }

    _disable() {
      if (this.elements.navigation !== null) {
        requestAnimationFrame(() => {
          this.elements.navigation.classList.add('hidden');
        });
      }

      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();

      this.progressBarAnimation?.cancel();

      clearTimeout(this.timeout);

      if (this.elements.navigation !== null) {
        this.elements.navigationButtonPrevious.removeEventListener('click', this._handleClickNavigationButton);
        this.elements.navigationButtonNext.removeEventListener('click', this._handleClickNavigationButton);
      }

      if (this.playState !== 'disabled') {
        for (let mouseTarget of this.elements.mouseTargets) {
          mouseTarget.removeEventListener('mouseenter', this._handleMouseEnter);
          mouseTarget.removeEventListener('mouseleave', this._handleMouseLeave);
        }

        this.removeEventListener('focusin', this._handleFocusIn);
        this.removeEventListener('focusout', this._handleFocusOut);
      }

      this.elements.list.removeAttribute('aria-live');
    }

    _initResizeObserver() {
      const callback = (entries) => {
        for (let entry of entries) {
          this._initIntersectionObserver(entry.contentRect.width);

          for (let child of this.elements.list.children) {
            this.intersectionObserver.observe(child);
          }
        }
      };

      this.resizeObserver = new ResizeObserver(callback);
    }

    _initIntersectionObserver(width) {
      this.intersectionObserver?.disconnect();

      const callback = (entries) => {
        let arr = [...this.visibleSlides];

        for (let entry of entries) {
          const position = parseInt(entry.target.dataset.position);

          if (entry.isIntersecting) {
            arr = [...arr, position];
          } else {
            arr = arr.filter((p) => p !== position);
          }
        }

        this.visibleSlides = [...new Set(arr)];
      };


      let rootMargin = ``;
      if (this.options.maximumWidth === 'full') {
        rootMargin = `0px`;
      } else {
        if (width < 768) {
          rootMargin = `0px -8px`;
        } else if (width < 1280) {
          rootMargin = `0px -12px`;
        } else if (width < 1440) {
          rootMargin = `0px -16px`;
        } else if (width < 1520) {
          rootMargin = `0px -20px`;
        } else {
          rootMargin = `0px -${Math.floor((width - 1480) / 2)}px`;
        }
      }

      const options = {
        root: this.elements.list,
        rootMargin: rootMargin,
        threshold: this.options.threshold,
      };

      this.intersectionObserver = new IntersectionObserver(callback, options);
    }

    _initMediaQuery() {
      const mediaQuery = window.matchMedia('(min-width: 768px)');

      const callback = () => {
        if (mediaQuery.matches) {
          this.viewport = 'md';
        } else {
          this.viewport = 'default';
        }
      };

      callback();
      mediaQuery.addEventListener('change', callback);
    }

    _updateProgressBarAnimation(visibleSlides) {
      if (visibleSlides.length === 0) {
        return;
      }

      if (this.playState === 'disabled') {
        return;
      }

      this.progressBarAnimation?.cancel();

      const i = visibleSlides[0];
      const currentSlide = this.elements.list.children[i];
      const progressBar = currentSlide.querySelector(this.selectors.progressBar);
      const keyframes = [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }];
      const options = { duration: this.options.duration, fill: 'both' };

      this.progressBarAnimation = progressBar.animate(keyframes, options);

      this.progressBarAnimation.addEventListener('finish', () => {
        let nextSlide = null;

        if (i < this.elements.list.children.length - 1) {
          nextSlide = this.elements.list.children[i + 1];
        } else {
          nextSlide = this.elements.list.children[0];
        }

        this.scrollToSlide(nextSlide);
      });

      if (this.playState !== 'running') {
        this.progressBarAnimation.pause();
      }
    }

    _updateTargetPosition(visibleSlides) {
      if (visibleSlides.length === 0) {
        return;
      }

      if (this.options.alignment == 'left' || this.options.alignment == 'center') {
        this.targetPosition = Math.min(...visibleSlides);
      }

      if (this.options.alignment == 'right') {
        this.targetPosition = Math.max(...visibleSlides);
      }
    }

    _updateNavigationList(oldVisibleSlides, newVisibleSlides) {
      if (newVisibleSlides.length === 0) {
        return;
      }

      let slidesToShow = 0;
      if (this.viewport === 'md') {
        slidesToShow = this.options.slidesToShowOnDesktop;
      } else {
        slidesToShow = this.options.slidesToShowOnMobile;
      }

      let direction = '';
      if (oldVisibleSlides.length === 0) {
        direction = 'right';
      } else if (Math.min(...oldVisibleSlides) < Math.min(...newVisibleSlides)) {
        direction = 'right';
      } else {
        direction = 'left';
      }

      if (this.elements.navigation !== null) {
        this._updateNavigationListColor(newVisibleSlides);
        this._updateNavigationListScale(newVisibleSlides, slidesToShow, direction);
        this._updateNavigationListTranslateX(newVisibleSlides, slidesToShow, direction);
      }
    }

    _updateNavigationListColor(visibleSlides) {
      for (let i = 0; i < this.elements.navigationList.children.length; i++) {
        const item = this.elements.navigationList.children[i];

        if (visibleSlides.includes(i)) {
          item.classList.replace('bg-body-text/25', 'bg-link');
        } else {
          item.classList.replace('bg-link', 'bg-body-text/25');
        }
      }
    }

    _updateNavigationListScale(visibleSlides, slidesToShow, direction) {
      if (this.options.variableWidthSlides) {
        for (let i = 0; i < this.totalSlides; i++) {
          const item = this.elements.navigationList.children[i];
          item.classList.replace('scale-100', 'scale-150');
        }
        return;
      }

      if (this.totalSlides <= slidesToShow + 4) {
        for (let i = 0; i < this.totalSlides; i++) {
          const item = this.elements.navigationList.children[i];
          item.classList.replace('scale-100', 'scale-150');
        }
        return;
      }

      if (direction === 'right') {
        const position = Math.max(...visibleSlides);

        const item = this.elements.navigationList.children[position + 1];
        if (item !== undefined && item.classList.contains('scale-150')) {
          return;
        }

        const boundLeft = slidesToShow + 2;
        const boundRight = this.totalSlides - 2;

        if (position <= boundLeft) {
          // Behavior A.
          for (let i = 0; i < this.totalSlides; i++) {
            const item = this.elements.navigationList.children[i];

            if (i <= slidesToShow + 2) {
              item.classList.replace('scale-100', 'scale-150');
            } else {
              item.classList.replace('scale-150', 'scale-100');
            }
          }
        } else if (position >= boundRight) {
          // Behavior B.
          for (let i = 0; i < this.totalSlides; i++) {
            const item = this.elements.navigationList.children[i];

            if (i >= this.totalSlides - slidesToShow - 3) {
              item.classList.replace('scale-100', 'scale-150');
            } else {
              item.classList.replace('scale-150', 'scale-100');
            }
          }
        } else {
          // Behavior C.
          for (let i = 0; i < this.totalSlides; i++) {
            const item = this.elements.navigationList.children[i];

            if (i >= position - slidesToShow - 1 && i <= position) {
              item.classList.replace('scale-100', 'scale-150');
            } else {
              item.classList.replace('scale-150', 'scale-100');
            }
          }
        }
      } else {
        const position = Math.min(...visibleSlides);

        const item = this.elements.navigationList.children[position - 1];
        if (item !== undefined && item.classList.contains('scale-150')) {
          return;
        }

        const boundRight = this.totalSlides - (slidesToShow + 2) - 1;
        const boundLeft = this.totalSlides - (this.totalSlides - 2) - 1;

        if (position >= boundRight) {
          // Behavior A.
          for (let i = this.totalSlides - 1; i >= 0; i--) {
            const item = this.elements.navigationList.children[i];

            if (i >= this.totalSlides - (slidesToShow + 2) - 1) {
              item.classList.replace('scale-100', 'scale-150');
            } else {
              item.classList.replace('scale-150', 'scale-100');
            }
          }
        } else if (position <= boundLeft) {
          // Behavior B.
          for (let i = this.totalSlides - 1; i >= 0; i--) {
            const item = this.elements.navigationList.children[i];

            if (i <= this.totalSlides - (this.totalSlides - slidesToShow - 3) - 1) {
              item.classList.replace('scale-100', 'scale-150');
            } else {
              item.classList.replace('scale-150', 'scale-100');
            }
          }
        } else {
          // Behavior C.
          for (let i = this.totalSlides - 1; i >= 0; i--) {
            const item = this.elements.navigationList.children[i];

            if (i >= position && i <= position + slidesToShow + 1) {
              item.classList.replace('scale-100', 'scale-150');
            } else {
              item.classList.replace('scale-150', 'scale-100');
            }
          }
        }
      }
    }

    _updateNavigationListTranslateX(newVisibleSlides, slidesToShow, direction) {
      if (this.options.variableWidthSlides) {
        return;
      }

      if (this.totalSlides <= slidesToShow + 4) {
        for (let i = 0; i < this.totalSlides; i++) {
          this.elements.navigationList.style.setProperty('--translate-x', `0px`);
        }
        return;
      }

      if (direction === 'right') {
        const position = Math.max(...newVisibleSlides);

        const item = this.elements.navigationList.children[position + 1];
        if (item !== undefined && item.classList.contains('scale-150') && position !== this.totalSlides - 2) {
          return;
        }

        const limit = Math.max(0, this.totalSlides - (slidesToShow + 4));

        if (position > slidesToShow + 2) {
          this.elements.navigationList.style.setProperty('--translate-x', `-${Math.min(limit, position - (slidesToShow + 2)) * 16}px`);
        }
      } else {
        const position = Math.min(...newVisibleSlides);

        const item = this.elements.navigationList.children[position - 1];
        if (item !== undefined && item.classList.contains('scale-150') && position !== 1) {
          return;
        }

        const limit = 0;

        if (position < this.totalSlides - (slidesToShow + 2) - 1 && position !== 0) {
          this.elements.navigationList.style.setProperty('--translate-x', `-${Math.max(limit, position - 1) * 16}px`);
        }
      }
    }

    _updateNavigationButtons(visibleSlides) {
      const min = Math.min(...visibleSlides);
      const max = Math.max(...visibleSlides);

      if (min === 0) {
        this.elements.navigationButtonPrevious.setAttribute('aria-disabled', 'true');
      } else {
        this.elements.navigationButtonPrevious.removeAttribute('aria-disabled');
      }

      if (max === this.totalSlides - 1) {
        this.elements.navigationButtonNext.setAttribute('aria-disabled', 'true');
      } else {
        this.elements.navigationButtonNext.removeAttribute('aria-disabled');
      }
    }

    _animateSlideText(oldVisibleSlides, newVisibleSlides) {
      let _oldVisibleSlides = oldVisibleSlides.filter(s => !newVisibleSlides.includes(s));
      let _newVisibleSlides = newVisibleSlides.filter(s => !oldVisibleSlides.includes(s));

      const oldSlideIndex = _oldVisibleSlides[0];
      const newSlideIndex = _newVisibleSlides[0];

      const oldSlide = this.elements.list.querySelector(`[data-position="${oldSlideIndex}"]`);
      const newSlide = this.elements.list.querySelector(`[data-position="${newSlideIndex}"]`);

      let direction = '';
      if (oldSlideIndex === Infinity) {
        direction = 'right';
      } else if (oldSlideIndex < newSlideIndex) {
        direction = 'right';
      } else {
        direction = 'left';
      }

      const oldKeyframes = [{ opacity: 1 }, { opacity: 0 }];

      const oldOptions = {
        duration: Themu.prefersReducedMotion ? 0 : 400,
        easing: Themu.animation.easeOut,
        fill: 'both',
      };

      const newKeyframes = [
        { opacity: 0, transform: direction === 'left' ? 'translateX(-64px)' : 'translateX(64px)' },
        { opacity: 1, transform: 'translateX(0)' },
      ];

      const newOptions = {
        duration: Themu.prefersReducedMotion ? 0 : 600,
        easing: Themu.animation.easeIn,
        fill: 'both',
      };

      if (oldSlide !== null) {
        const animationTargets = oldSlide.querySelectorAll(this.selectors.listItemAnimationTarget);
        for (let animationTarget of animationTargets) {
          const animation = animationTarget.animate(oldKeyframes, oldOptions);
          animation.addEventListener('finish', () => {
            rafSchd(() => {
              animation.commitStyles();
              animation.cancel();
            });
          });
        }
      }

      if (newSlide !== null) {
        const animationTargets = newSlide.querySelectorAll(this.selectors.listItemAnimationTarget);
        for (let i = 0; i < animationTargets.length; i++) {
          const animationTarget = animationTargets[i];
          const animation = animationTarget.animate(newKeyframes, { ...newOptions, delay: 100 + 50 * i });
          animation.addEventListener('finish', () => {
            rafSchd(() => {
              animation.commitStyles();
              animation.cancel();
            });
          });
        }
      }
    }

    _updateAriaAttributes(visibleSlides) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        for (let i = 0; i < this.totalSlides; i++) {
          const slide = this.elements.list.children[i];
        }
      }, 500);
    }

    _handleMouseEnter() {
      this.hasMouseFocus = true;

      if (this.playState !== 'disabled') {
        this.playState = 'paused';
      }
    }

    _handleMouseLeave() {
      this.hasMouseFocus = false;

      if (document.activeElement.hasAttribute('data-slideshow-navigation-button')) {
        document.activeElement.blur();
      }

      if (this.playState !== 'disabled' && !this.hasKeyboardFocus) {
        this.playState = 'running';
      }
    }

    _handleFocusIn() {
      this.hasKeyboardFocus = true;

      if (this.playState !== 'disabled') {
        this.playState = 'paused';
      }
    }

    _handleFocusOut() {
      this.hasKeyboardFocus = false;

      if (this.playState !== 'disabled' && !this.hasMouseFocus) {
        this.playState = 'running';
      }
    }

    _handleClickNavigationButton(event) {
      const button = event.target.closest(this.selectors.navigationButton);

      if (button.hasAttribute('aria-disabled')) {
        event.preventDefault();
        return;
      }

      if (button.dataset.action === 'previous') {
        if (this.targetPosition === 0) {
          event.preventDefault();
          return;
        }

        this.targetPosition--;
      } else {
        let last = 0;
        if (this.options.variableWidthSlides) {
          last = this.totalSlides - 1;
        } else if (this.viewport === 'default') {
          last = this.totalSlides - this.options.slidesToShowOnMobile;
        } else {
          last = this.totalSlides - this.options.slidesToShowOnDesktop;
        }

        if (this.targetPosition === last) {
          event.preventDefault();
          return;
        }

        this.targetPosition++;
      }

      const nextSlide = this.elements.list.querySelector(`[data-position="${this.targetPosition}"]`);
      this.scrollToSlide(nextSlide);
    }

    removeSlide(position) {
      this.visibleSlides = this.visibleSlides.filter((s) => s !== parseInt(position));
    }

    scrollToSlide(slide) {
      const marginLeft = getComputedStyle(this.elements.list.children[0]).getPropertyValue('margin-left');
      const offset = parseFloat(marginLeft.replace('px', ''));

      let left = 0;
      switch (this.options.alignment) {
        case 'left':
          left = slide.offsetLeft - offset;
          break;
        case 'center':
          left = slide.offsetLeft - this.elements.list.offsetWidth / 2 + slide.offsetWidth / 2;
          break;
        case 'right':
          left = slide.offsetLeft + offset - this.elements.list.offsetWidth + slide.offsetWidth;
          break;
      }

      this.elements.list.scrollTo({
        left: left,
        top: 0,
        behavior: Themu.prefersReducedMotion ? 'instant' : 'smooth',
      });
    }
  }

  customElements.define('ce-slideshow', Slideshow);
})();
